import { useState } from "react";
import { Platform } from "react-native";
import axios from "axios";
import { getRazorpayKey, isFromAndroidWebview, isIframeCheck, RAZOR_PAY_PREFERRED_METHOD } from "@app/utils";
import ROUTE from "@app/navigationApp/routes";
import { DSP_PAYMENT_STATUS } from "@app/types/regix";
import RazorpayCheckout from "react-native-razorpay";
import { endpoints } from "@app/api/apis";
import { useAppDispatch } from "@app/store/hooks";
import { showErrorSheet } from "@app/store/slices/errorSlice";
import handleApiErr from "@app/helper/handleApiErr";

interface UserDetails {
    user: {
        fullName: string;
        phoneNumber: string;
    };
    linkedBorrowerAccounts: Array<{
        accountHolderEmail: string;
    }>;
}

interface WebPaymentParams {
    preferredMethod: string[];
    collectionId: string;
    color: string;
    timeout: number;
    currentOrderId: string;
    key: string;
    redirectingRoute: string;
    navigation: any;
    userDetails: UserDetails;
    selectedAmount: number;
}

const useDSPPayment = () => {
    const [loading, setLoading] = useState(false);
    const dispatch = useAppDispatch();
    const startDSPPayment = async (
        selectedAmount: number,
        creditId: string,
        paymentType: string,
        isDecreaseForeclosureFlow: boolean,
        navigation: any,
        userDetails: UserDetails
    ): Promise<void> => {
        setLoading(true);
        const payload = {
            baseOriginUrl: window?.location?.href,
            paymentGatewayIntegrationType: 'PG_SDK',
            collectionAmount: `${selectedAmount}`,
            creditId: creditId,
            redirect: Platform.OS === 'web' && !isIframeCheck() && !isFromAndroidWebview(),
            collectionType: paymentType || "PRINCIPAL_REPAYMENT",
            isDecreaseForeclosureFlow: isDecreaseForeclosureFlow,
        };

        axios.post(endpoints.paymentInit, payload)
            .then((response) => {
                    const timeout = 600;
                    const collectionId = response?.data?.collectionId;
                    const currentOrderId = response?.data?.pgPaymentOrderId;
                    const key = getRazorpayKey();
                    const color = '#1434CB';
                    let preferredMethod = [''];
                    const redirectingRoute = ROUTE.FLEXI_PAY;

                    if (selectedAmount <= 2000) {
                        preferredMethod = RAZOR_PAY_PREFERRED_METHOD.LESS_THAN_100K;
                    } else if (selectedAmount <= 100000) {
                        preferredMethod = RAZOR_PAY_PREFERRED_METHOD.LESS_THAN_100K;
                    } else {
                        preferredMethod = RAZOR_PAY_PREFERRED_METHOD.DEFAULT;
                    }

                    if (Platform.OS === 'web') {
                        return handleWebPayment({
                            preferredMethod,
                            collectionId,
                            color,
                            timeout,
                            currentOrderId,
                            key,
                            redirectingRoute,
                            navigation,
                            userDetails,
                            selectedAmount
                        });
                    } else {
                        return handleMobilePayment(
                            userDetails,
                            selectedAmount,
                            color,
                            preferredMethod,
                            collectionId,
                            timeout,
                            currentOrderId,
                            key,
                            navigation
                        );
                    }
            })
            .catch((err) => {
                console.error('DSP Payment Error:', err);
                if(handleApiErr(err)){
                    dispatch(showErrorSheet({
                        isErrorSheetVisible: true,
                        errorMessage: err?.data?.message,
                        closeOnBackdropPress: true,
                        errorType: err?.data?.voltErrorCode || err?.data?.statusCode,
                        errorHeading: err?.data?.header,
                        showErrorHeading: true,
                        btnName:'Ok, got it!',
                    }))
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleWebPayment = async ({
        preferredMethod,
        collectionId,
        color,
        timeout,
        currentOrderId,
        key,
        redirectingRoute,
        navigation,
        userDetails,
        selectedAmount
    }: WebPaymentParams): Promise<void> => {
        const res = await loadRazorpayScript('https://checkout.razorpay.com/v1/checkout.js');
        if (!res) return;

        const options = {
            description: '',
            image: "https://volt-images-alpha.s3.ap-south-1.amazonaws.com/Favicon.png",
            currency: "INR",
            key: key,
            amount: `${selectedAmount * 100}`,
            name: "DSP Finance Pvt Ltd",
            order_id: currentOrderId,
            prefill: {
                name: `${userDetails?.user?.fullName}`,
                email: `${userDetails?.linkedBorrowerAccounts[0]?.accountHolderEmail}`,
                contact: `${userDetails?.user?.phoneNumber}`,
            },
            method: {
                upi: true,
            },
            retry: {
                enabled: true,
                max_count: 4,
            },
            theme: {
                color: color,
            },
            modal: {
                ondismiss: () => {
                    navigation.navigate(ROUTE.PAYMENT_STATUS, {
                        collectionId,
                        dspPaymentStatus: DSP_PAYMENT_STATUS.CANCELLED,
                        currentOrderId,
                        redirectingRoute
                    });
                }
            },
            handler: () => {
                navigation.navigate(ROUTE.PAYMENT_STATUS, { collectionId, dspPaymentStatus: DSP_PAYMENT_STATUS.SUCCESS });
            },
        };

        //@ts-ignore
        const rzp = new Razorpay(options);
        rzp.open();
    };

    const handleMobilePayment = async (
        userDetails: UserDetails,
        selectedAmount: number,
        color: string,
        preferredMethod: string[],
        collectionId: string,
        timeout: number,
        currentOrderId: string,
        key: string,
        navigation: any
    ) => {
        const options = {
            description: '',
            image: "https://volt-images-alpha.s3.ap-south-1.amazonaws.com/Favicon.png",
            currency: "INR",
            key: key,
            amount: `${selectedAmount * 100}`,
            name: "DSP Finance Pvt Ltd",
            order_id: currentOrderId,
            prefill: {
                name: userDetails?.user?.fullName,
                email: userDetails?.linkedBorrowerAccounts[0]?.accountHolderEmail,
                contact: userDetails?.user?.phoneNumber,
            },
            method: {
                netbanking: preferredMethod.includes('netbanking'),
                upi: preferredMethod.includes('upi'),
                card: preferredMethod.includes('card'),
            },
            theme: {
                color: color,
            },
            timeout: timeout,
            retry: {
                enabled: true,
                max_count: 4,
            },
            modal: {
                ondismiss: () => {
                    navigation.navigate(ROUTE.PAYMENT_STATUS, {
                        collectionId,
                        dspPaymentStatus: DSP_PAYMENT_STATUS.CANCELLED,
                        currentOrderId,
                    });
                }
            }
        };

        try {
            // @ts-ignore
            await RazorpayCheckout.open(options);
        } catch (error) {
            navigation.navigate(ROUTE.PAYMENT_STATUS, {
                collectionId,
                dspPaymentStatus: DSP_PAYMENT_STATUS.CANCELLED,
                currentOrderId,
            });
        }
    };

    const loadRazorpayScript = (src: string): Promise<boolean> => {
        return new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = src;
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    return { startDSPPayment, loading };
};

export default useDSPPayment;
